import type { LevelingComparisonProps } from './types.js';

function formatDelay(minutes: number): string {
  if (minutes <= 0) return '—';
  const hours = minutes / 60;
  const days = hours / 8;
  return days >= 1 ? `${days.toFixed(1)} 天` : `${hours.toFixed(1)} 小時`;
}

/**
 * 撫平前後對照：每個被延後任務的原始日期 → 撫平後日期 → 延後量，
 * 以及專案完成日的變化與剩餘過度分配數。純展示元件。
 */
export function LevelingComparison(props: LevelingComparisonProps): React.JSX.Element {
  const { rows, originalFinish, leveledFinish, finishDelayMinutes, remainingOverallocationCount } = props;

  return (
    <div data-testid="leveling-comparison" className="flex h-full w-full flex-col overflow-auto bg-surface text-sm text-fg">
      <div className="grid grid-cols-3 gap-3 border-b border-line p-3">
        <div>
          <div className="text-xs text-fg-muted">原始完成日</div>
          <div data-testid="leveling-original-finish">{originalFinish.slice(0, 10)}</div>
        </div>
        <div>
          <div className="text-xs text-fg-muted">撫平後完成日</div>
          <div data-testid="leveling-leveled-finish">{leveledFinish.slice(0, 10)}</div>
        </div>
        <div>
          <div className="text-xs text-fg-muted">完成日延後</div>
          <div
            data-testid="leveling-finish-delay"
            className={finishDelayMinutes > 0 ? 'text-red-600 dark:text-danger' : ''}
          >
            {formatDelay(finishDelayMinutes)}
          </div>
        </div>
      </div>

      <div
        data-testid="leveling-remaining-overallocations"
        className={[
          'border-b border-line px-3 py-2 text-xs',
          remainingOverallocationCount > 0 ? 'text-red-600 dark:text-danger' : 'text-fg-muted',
        ].join(' ')}
      >
        剩餘過度分配：{remainingOverallocationCount} 筆
      </div>

      <div className="flex border-b border-line-strong bg-raised font-medium">
        <div className="w-56 shrink-0 border-r border-line px-2 py-1">任務</div>
        <div className="w-40 shrink-0 border-r border-line px-2 py-1">原始開始日</div>
        <div className="w-40 shrink-0 border-r border-line px-2 py-1">撫平後開始日</div>
        <div className="w-32 shrink-0 px-2 py-1">延後量</div>
      </div>

      {rows.map((row) => (
        <div key={row.taskId} role="row" data-testid={`leveling-row-${row.taskId}`} className="flex border-b border-hairline">
          <div className="w-56 shrink-0 truncate border-r border-hairline px-2 py-1">{row.taskName}</div>
          <div className="w-40 shrink-0 border-r border-hairline px-2 py-1">{row.originalStart.slice(0, 10)}</div>
          <div className="w-40 shrink-0 border-r border-hairline px-2 py-1">{row.leveledStart.slice(0, 10)}</div>
          <div className="w-32 shrink-0 px-2 py-1">{formatDelay(row.delayMinutes)}</div>
        </div>
      ))}
    </div>
  );
}

export default LevelingComparison;
